/**
 * Briefing Generator Component
 * Form for generating new executive briefings
 * Part 3, Phase 5 - Executive Briefing UI
 *
 * Features:
 * - Module selection (multi-select)
 * - Date range picker
 * - Briefing type selection
 * - Generate button with progress indicator
 */

import React, { useState } from 'react';
import {
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Alert,
  Progress,
  Steps
} from 'antd';
import {
  CheckCircleOutlined,
  LoadingOutlined,
  FileTextOutlined,
  CalendarOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import execute from '../../../hooks/execute';
import { protectedResources } from '../../../config/msalConfig';
import { useUser } from '../../../components/UserContext';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Step } = Steps;

const BriefingGenerator = ({ visible, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { userDetails } = useUser();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generationStep, setGenerationStep] = useState(0);
  const [generatedBriefing, setGeneratedBriefing] = useState(null);

  // Use user's accessible modules from userDetails
  const modules = userDetails?.Modules || [];

  /**
   * Handle form submission
   */
  const handleGenerate = async (values) => {
    try {
      setLoading(true);
      setError(null);
      setGenerationStep(1);

      // Prepare request payload
      const payload = {
        organizationId: userDetails.CustomerID,
        briefingType: values.briefingType || 'ON_DEMAND',
        title: values.title,
        moduleIds: values.moduleIds || null
      };

      // Add date range if provided
      if (values.dateRange && values.dateRange.length === 2) {
        payload.startDate = values.dateRange[0].toISOString();
        payload.endDate = values.dateRange[1].toISOString();
      }

      // Simulate progress steps
      setTimeout(() => setGenerationStep(2), 1000);
      setTimeout(() => setGenerationStep(3), 2000);

      // Make API call
      const endpoint = `${protectedResources.api.baseEndPoint}/v1/executive/briefing/generate`;
      const response = await execute('POST', endpoint, payload);

      if (response.success) {
        setGenerationStep(4);
        setGeneratedBriefing(response.data);

        // Call success callback
        if (onSuccess) {
          onSuccess(response.data);
        }

        // Navigate to briefing page after 2 seconds
        setTimeout(() => {
          navigate(`/executive-briefing/${response.data.BriefingId}`);
          handleClose();
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to generate briefing');
      }
    } catch (err) {
      console.error('[BriefingGenerator] Generation failed:', err);
      setError(err.message || 'Failed to generate briefing');
      setGenerationStep(0);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (!loading) {
      form.resetFields();
      setError(null);
      setGenerationStep(0);
      setGeneratedBriefing(null);
      onClose();
    }
  };

  /**
   * Render generation steps
   */
  const renderGenerationSteps = () => {
    if (!loading && generationStep === 0) return null;

    return (
      <div className="mb-6">
        <Steps current={generationStep} size="small">
          <Step title="Initializing" icon={generationStep === 1 ? <LoadingOutlined /> : null} />
          <Step
            title="Aggregating Data"
            icon={generationStep === 2 ? <LoadingOutlined /> : null}
          />
          <Step
            title="Generating Insights"
            icon={generationStep === 3 ? <LoadingOutlined /> : null}
          />
          <Step title="Complete" icon={generationStep === 4 ? <CheckCircleOutlined /> : null} />
        </Steps>

        {loading && (
          <div className="mt-4">
            <Progress percent={generationStep * 25} status="active" />
            <div className="text-center text-gray-600 mt-2">
              {generationStep === 1 && 'Preparing aggregation...'}
              {generationStep === 2 && 'Collecting data from modules...'}
              {generationStep === 3 && 'Generating AI insights...'}
            </div>
          </div>
        )}

        {generationStep === 4 && generatedBriefing && (
          <Alert
            message="Briefing Generated Successfully"
            description={`Your executive briefing "${generatedBriefing.Title}" has been created. Redirecting...`}
            type="success"
            icon={<CheckCircleOutlined />}
            showIcon
            className="mt-4"
          />
        )}
      </div>
    );
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <FileTextOutlined />
          Generate Executive Briefing
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="generate"
          type="primary"
          onClick={() => form.submit()}
          loading={loading}
          disabled={generationStep === 4}
        >
          {loading ? 'Generating...' : 'Generate Briefing'}
        </Button>
      ]}
    >
      {/* Generation Progress */}
      {renderGenerationSteps()}

      {/* Error Alert */}
      {error && (
        <Alert
          message="Generation Failed"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}

      {/* Generation Form */}
      {!loading && generationStep === 0 && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleGenerate}
          initialValues={{
            briefingType: 'ON_DEMAND',
            dateRange: [dayjs().subtract(30, 'days'), dayjs()]
          }}
        >
          {/* Briefing Title */}
          <Form.Item
            label="Briefing Title"
            name="title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input
              prefix={<FileTextOutlined />}
              placeholder="e.g., Q4 2025 Executive Summary"
              maxLength={200}
            />
          </Form.Item>

          {/* Briefing Type */}
          <Form.Item
            label="Briefing Type"
            name="briefingType"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select placeholder="Select briefing type">
              <Option value="WEEKLY">Weekly Briefing</Option>
              <Option value="MONTHLY">Monthly Briefing</Option>
              <Option value="QUARTERLY">Quarterly Briefing</Option>
              <Option value="ON_DEMAND">On-Demand Custom Briefing</Option>
            </Select>
          </Form.Item>

          {/* Date Range */}
          <Form.Item
            label="Date Range"
            name="dateRange"
            tooltip="Select the time period to analyze"
          >
            <RangePicker
              className="w-full"
              prefix={<CalendarOutlined />}
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current > dayjs().endOf('day')}
            />
          </Form.Item>

          {/* Module Selection */}
          <Form.Item
            label="Modules"
            name="moduleIds"
            tooltip="Leave empty to include all accessible modules"
          >
            <Select
              mode="multiple"
              placeholder="Select modules (optional - defaults to all)"
              notFoundContent="No modules available"
              maxTagCount="responsive"
            >
              {modules.map((module) => (
                <Option key={module.ModuleId} value={module.ModuleId}>
                  {module.ModuleName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
            <div className="text-sm text-gray-700">
              <div className="font-medium mb-2">What's Included:</div>
              <ul className="list-disc list-inside space-y-1">
                <li>Executive summary of key findings</li>
                <li>Overall health score and risk assessment</li>
                <li>Cross-module trends and patterns</li>
                <li>Strategic recommendations</li>
                <li>Key performance metrics</li>
              </ul>
            </div>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default BriefingGenerator;
